'use client';

import { useEffect, useRef, useState } from 'react';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { useWindowVirtualizer } from '@tanstack/react-virtual';

import useResizeObserver from 'use-resize-observer';

import useCustomTooltip from '@/lib/custom-tooltip';
import { queryFunctions } from '@/lib/query-functions';

import { Entry } from '@/types/cit-public-api';

import { useCitOriginClient } from '@/app/CitOriginClientProvider';

import EntryCard from './EntryCard';

// Constants
const GAP = 20;
const IMAGE_SIZE = 150;
const OVERSCAN_MULTIPLIER = 2; // virtualize 'n' additional rows above & 'n' additional rows below the viewport
const MIN_COLUMNS = 2;

interface ScrolledEntriesDisplayProps {
  accountId?: string;
  folderId?: string;
  classificationId?: string;
  searchQuery: string | null;
}

function PaginatedEntriesDisplay({
  accountId,
  folderId,
  classificationId,
  searchQuery
}: ScrolledEntriesDisplayProps) {
  const citOrigin = useCitOriginClient();

  const [columnCount, setColumnCount] = useState(8);
  const [imageSize, setImageSize] = useState(IMAGE_SIZE);

  const { customTooltip, handleHideToolTip, handleShowToolTip } = useCustomTooltip();

  const [textBoxHeight, setTextBoxHeight] = useState(0);

  const [slots, setSlots] = useState<boolean[]>([true, true, false]);

  const listRef = useRef<HTMLDivElement>(null);
  const textBoxRef = useRef<HTMLDivElement>(null);

  const { data, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: [
      'paginated-entries',
      {
        accountId,
        folderId,
        classificationId,
        searchQuery,
        citOrigin
      }
    ],
    initialPageParam: undefined,
    getNextPageParam: (lastPage: { entries: Entry[]; total: number; cursor?: string }) => {
      // Only return cursor if we have entries and a valid cursor
      if (lastPage.entries?.length > 0 && lastPage.cursor) {
        return lastPage.cursor;
      }
      return undefined;
    },
    queryFn: queryFunctions.fetchEntriesWithPagination
  });

  const { data: accountDetails } = useQuery({
    queryKey: ['account-details', { accountId: accountId || '', citOrigin }],
    queryFn: queryFunctions.getAccountDetails,
    enabled: !!accountId
  });

  const mediaAddornment = accountDetails?.media?.media_adornment || 'rounded';

  useEffect(() => {
    if (accountDetails?.customization?.thumbnail) {
      setSlots([
        !!accountDetails.customization?.thumbnail?.slot1,
        !!accountDetails.customization?.thumbnail?.slot2,
        !!accountDetails.customization?.thumbnail?.slot3
      ]);
    }
  }, [accountDetails, setSlots]);

  const allEntries = data ? data.pages.flatMap((d) => d.entries) : [];

  const { width: containerWidth = 0 } = useResizeObserver<HTMLDivElement>({
    ref: listRef.current
  });

  // used over just a normal ref because of its ability to detect when the browswer font size changes
  const { height: observedTextBoxHeight = 0 } = useResizeObserver<HTMLDivElement>({
    ref: textBoxRef.current
  });

  const virtualizer = useWindowVirtualizer({
    count: hasNextPage ? allEntries.length + 1 : allEntries.length,
    estimateSize: () => imageSize + textBoxHeight + GAP,
    overscan: columnCount * OVERSCAN_MULTIPLIER,
    lanes: columnCount
  });

  useEffect(() => {
    // automatically scroll back to the top when a new search is made
    virtualizer.scrollToIndex(0);
  }, [searchQuery, virtualizer]);

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    if (containerWidth > 0) {
      // Calculate available width accounting for padding
      const availableWidth = containerWidth;
      // Calculate how many columns can fit including gaps
      const cols = Math.max(MIN_COLUMNS, Math.floor((availableWidth + GAP) / (IMAGE_SIZE + GAP)));
      // Calculate the actual column width to fill space evenly
      const colWidth = Math.floor((availableWidth - (cols - 1) * GAP) / cols);

      setColumnCount(cols);
      setImageSize(colWidth);

      setTextBoxHeight(observedTextBoxHeight);

      virtualizer.measure();
    }
  }, [containerWidth, virtualizer, observedTextBoxHeight]);

  useEffect(() => {
    const [lastItem] = [...virtualItems].reverse();

    if (!lastItem) {
      return;
    }

    if (lastItem.index >= allEntries.length - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, isFetchingNextPage, virtualItems, allEntries.length]);

  // scroll to the top of the page when the folderId changes or navigated to from a different page, Link component is supposed to do this automatically but it doesn't for some reason
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [folderId]);

  return (
    <>
      {customTooltip}
      {searchQuery ? (
        <div className='flex items-center h-16'>
          <h1 className='text-xl font-semibold'>Search results for &quot;{searchQuery}&quot;</h1>
        </div>
      ) : null}

      <div ref={listRef} style={{ display: 'flex', justifyContent: 'center' }}>
        <div
          style={{
            position: 'relative',
            height: `${virtualizer.getTotalSize()}px`,
            width: `${columnCount * imageSize + (columnCount - 1) * GAP}px`,
            minHeight: '100px'
          }}
        >
          {virtualItems.map((virtualItem) => {
            const entry = allEntries[virtualItem.index];
            // Skip rendering if we don't have an entry and we're not loading more
            if (!entry) {
              return null;
            }

            const col = virtualItem.index % columnCount;

            return (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'absolute',
                  width: `${imageSize}px`,
                  height: `${imageSize + textBoxHeight}px`,
                  // need to calculate x position ourselves, y position is handled by virtualizer
                  transform: `translate(${col * (imageSize + GAP)}px, ${virtualItem.start}px)`
                }}
                key={virtualItem.key}
                data-index={virtualItem.index}
                className='entry'
              >
                <EntryCard
                  classificationId={classificationId}
                  folderId={folderId}
                  account={accountDetails}
                  entry={entry}
                  searchQuery={searchQuery}
                  handleHideToolTip={handleHideToolTip}
                  handleShowToolTip={handleShowToolTip}
                  imageSize={imageSize}
                  textBoxHeight={textBoxHeight}
                  mediaAddornment={mediaAddornment}
                  prefix={citOrigin === 'iframe' ? 'iframe' : undefined}
                />
              </div>
            );
          })}
          {/* show a loading symbol if theres a next page and we're fetching it */}
          {/* TODO: replace 'Loading...' with an actual loading symbol */}
          {hasNextPage && isFetchingNextPage && !isError ? (
            <div
              style={{
                position: 'absolute',
                width: '100%',
                textAlign: 'center',
                // display in the center of where last row of entries would be
                transform: `translateY(${
                  virtualItems[virtualItems.length - 1].start + (imageSize + textBoxHeight) / 2
                }px)`
              }}
            >
              Loading...
            </div>
          ) : null}
          {isError ? (
            <div
              style={{
                position: 'absolute',
                width: '100%',
                textAlign: 'center',
                // display in the center of where last row of entries would be
                transform: `translateY(${
                  virtualItems[virtualItems.length - 1].start + (imageSize + textBoxHeight) / 2
                }px)`
              }}
            >
              Something went wrong...
            </div>
          ) : null}
        </div>
      </div>
      {/*  hidden copy of entry slot div for measuring text box size */}
      <div
        ref={textBoxRef}
        style={{
          position: 'absolute',
          left: '-9999px',
          visibility: 'hidden',
          width: `${imageSize}px`
        }}
        aria-hidden='true'
      >
        {slots[0] ? <h6 className='slot1'>slot1</h6> : null}
        {slots[1] ? <p className='slot2'>slot2</p> : null}
        {slots[2] ? <p className='slot3'>slot3</p> : null}
      </div>
    </>
  );
}

export default function ScrolledEntriesDisplay(props: ScrolledEntriesDisplayProps) {
  return (
    <>
      {/* <ReactQueryDevtools initialIsOpen={false} /> */}
      <PaginatedEntriesDisplay {...props} />
    </>
  );
}
